import React, { useState, useContext, useEffect } from "react";
import { useSiteConfig } from "../context/SiteConfigContext";
import { Link } from "react-router-dom";
import CartCanvas from "../pages/canvas/CartCanvas";
import LoginCanvas from "../pages/canvas/LoginCanvas";
import RegisterCanvas from "../pages/canvas/RegisterCanvas";
import Auth from "../utils/AuthUtils";
import { useCart } from "../context/CartContext";
import SearchCanvas from "../pages/canvas/SearchCanvas";
import { Container, Nav } from "react-bootstrap";
import { Menu, User, Search, Heart, ShoppingBag, X } from "lucide-react";
import {
  FaFacebook,
  FaInstagram,
  FaLinkedin,
  FaTwitter,
  FaYoutube,
} from "react-icons/fa";
import { CategoryContext } from "../context/CategoryContext";

function Header() {
  const [searchText, setSearchText] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const { cartItems } = useCart();
  const { config } = useSiteConfig();
  const isLoggedIn = Auth.getToken();
  const { categories } = useContext(CategoryContext);
  const [openId, setOpenId] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);

  const handleToggle = (id) => {
    setOpenId(openId === id ? null : id);
  };

  const closeSearch = () => {
    setShowSearch(false);
    setSearchText("");
  };

  // close search overlay on Escape
  useEffect(() => {
    if (!showSearch) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeSearch();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [showSearch]);

  // glassmorphism by default, solid white once user scrolls
  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (!config) return null;

  return (
    <>
      {/* MAIN HEADER */}

      <header
        className={`custom-header-wrapper ${
          isScrolled ? "header-scrolled" : ""
        }`}
      >
        <div className="custom-header-main-layer py-2 border-bottom">
          <Container fluid className="px-md-5 px-3">
            <div className="custom-header-flex">
              {/* LEFT — hamburger (mobile) + logo (desktop) */}
              <div className="custom-header-left">
                <button
                  data-bs-toggle="offcanvas"
                  data-bs-target="#mobileMenu"
                  className="custom-mobile-menu-btn d-lg-none"
                >
                  <Menu size={24} />
                </button>

                <Link to="/" className="custom-header-logo d-none d-lg-flex">
                  <img src={config?.logo} alt="logo" />
                </Link>
              </div>

              {/* CENTER — logo (mobile) + nav links (desktop) */}
              <div className="custom-header-center">
                <Link to="/" className="custom-header-logo d-flex d-lg-none">
                  <img src={config?.logo} alt="logo" />
                </Link>

                <Nav className="custom-header-nav d-none d-lg-flex">
                  <Nav.Link as={Link} to="/" className="custom-nav-item">
                    Home
                  </Nav.Link>

                  <Nav.Link as={Link} to="/about-us" className="custom-nav-item">
                    About Us
                  </Nav.Link>

                  <Nav.Link as={Link} to="/shop" className="custom-nav-item">
                    Shop
                  </Nav.Link>               
               

                  
                  <div className="custom-nav-dropdown">
                    <Nav.Link as={Link} to="#" className="custom-nav-item">
                      Categories{" "}
                      <i
                        className="fas fa-chevron-down ms-1"
                        style={{ fontSize: "10px" }}
                      ></i>
                    </Nav.Link>

                    <div className="custom-dropdown-menu">
                      {categories.map((cat) => (
                        <div key={cat._id} className="custom-category-group">
                          <Link
                            to={`/categories/${cat.slugPath}`}
                            className="custom-dropdown-item"
                          >
                            {cat.name}
                          </Link>

                          
                        </div>
                      ))}
                    </div>
                  </div>
                    <Nav.Link as={Link} to="/blog" className="custom-nav-item">
                    Blogs
                  </Nav.Link>
                  <Nav.Link
                    as={Link}
                    to="/contact-us"
                    className="custom-nav-item"
                  >
                    Contact
                  </Nav.Link>
                </Nav>
              </div>

              {/* RIGHT — search trigger + wishlist + account + cart */}
              <div className="custom-header-right">
                <button
                  type="button"
                  className="custom-header-link icon-link search-trigger-btn"
                  onClick={() => setShowSearch(true)}
                  aria-label="Open search"
                >
                  <Search size={20} />
                </button>

                <Link to="/wishlist" className="custom-header-link icon-link">
                  <Heart size={20} />
                </Link>

                <div className="d-none d-lg-flex">
                  {isLoggedIn ? (
                    <Link
                      to="/users/dashboard"
                      className="custom-header-link icon-link"
                    >
                      <User size={20} />
                    </Link>
                  ) : (
                    <Link
                      to="/users/login"
                      className="custom-header-link icon-link"
                    >
                      <User size={20} />
                    </Link>
                  )}
                </div>

                <button
                  data-bs-toggle="offcanvas"
                  data-bs-target="#shoppingCartCanvas"
                  className="custom-header-link bg-transparent border-0 p-0 custom-cart-icon icon-link"
                >
                  <ShoppingBag size={20} />
                  <span className="cart-count">{cartItems.length}</span>
                </button>
              </div>
            </div>
          </Container>
        </div>

        {/* SEARCH OVERLAY — click outside or X to close */}
        {showSearch && (
          <SearchCanvas
            isOpen={showSearch}
            searchText={searchText}
            setSearchText={setSearchText}
            closeSearch={closeSearch}
          />
        )}
      </header>

      <div
        className="offcanvas offcanvas-end custom-cart-offcanvas"
        id="shoppingCartCanvas"
      >
        <CartCanvas />
      </div>

      <div
        className="offcanvas offcanvas-end popup-style-1 popup-shopping-cart"
        id="loginOffCanvas"
      >
        <LoginCanvas />
      </div>

      <div
        className="offcanvas offcanvas-end popup-style-1 popup-shopping-cart"
        id="registerOffCanvas"
      >
        <RegisterCanvas />
      </div>

      <div className="offcanvas offcanvas-start canvas-mb" id="mobileMenu">
        <button
          className="icon-close icon-close-popup"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
        />
        <div className="mb-canvas-content">
          <div className="mb-body">
            <div className="mb-content-top">
              <ul className="nav-ul-mb" id="wrapper-menu-navigation">
                <li className="nav-mb-item">
                  <Link to="/" className="mb-menu-link">
                    Home
                  </Link>
                </li>

                {/* ABOUT DROPDOWN (Toggleable) */}
                <li className="nav-mb-item">
                  <div
                    className="mb-menu-link d-flex justify-content-between align-items-center"
                    onClick={() => handleToggle("about-mb")}
                    style={{ cursor: "pointer" }}
                  >
                    About
                    <span className="plus-minus-icon">
                      {openId === "about-mb" ? "-" : "+"}
                    </span>
                  </div>

                  {openId === "about-mb" && (
                    <div className="dropdown-menu show border-0 ps-3 bg-transparent">
                      <Link to="/about-us" className="dropdown-item py-2">
                        About Us
                      </Link>
                      <Link to="/blog" className="dropdown-item py-2">
                        Blogs
                      </Link>
                      <Link to="/shop" className="dropdown-item py-2">
                        Shop
                      </Link>
                    </div>
                  )}
                </li>

                {categories.map((cat) => (
                  <li className="nav-mb-item" key={cat._id}>
                    <div
                      className="mb-menu-link nav-item-dropdown d-flex justify-content-between"
                      onClick={() => handleToggle(cat._id)}
                    >
                      {cat.name}
                      <span>{openId === cat._id ? "-" : "+"}</span>
                    </div>
                    {cat.children?.length > 0 && openId === cat._id && (
                      <div className="dropdown-menu show border-0 ps-3 bg-transparent">
                        {cat.children.map((sub) => (
                          <Link
                            key={sub._id}
                            to={`/categories/${sub.slugPath}`}
                            className="dropdown-item"
                          >
                            {sub.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </li>
                ))}
                <li className="nav-mb-item">
                  <Link to="/contact-us" className="mb-menu-link">
                    Contact
                  </Link>
                </li>
              </ul>
            </div>
            <div className="mb-other-content">
              <div className="group-icon">
                {!isLoggedIn ? (
                  <Link to="/users/login" className="shop-btn">
                    <i className="icon icon-user me-2" /> Login
                  </Link>
                ) : (
                  <Link to="/users/dashboard" className="shop-btn">
                    <i className="icon icon-user me-2" /> Dashboard
                  </Link>
                )}
              </div>
              <div className="mb-contact mb-0 border-bottom border-muted">
                <p>
                  Address:
                  <span className="ms-1">
                    {[
                      config?.address?.addressLine,
                      config?.address?.district,
                      config?.address?.state,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                    {config?.address?.pincode
                      ? ` - ${config?.address.pincode}`
                      : ""}
                  </span>
                </p>
              </div>
              <ul className="pt-3">
                <li>
                  Email:
                  <b className="fw-medium">
                    <a
                      className="text-decoration-none text-secondary"
                      href={`mailto: ${config.email}`}
                    >
                      {config.email}
                    </a>
                  </b>
                </li>
                <li>
                  Phone:
                  <b className="fw-medium">
                    <a
                      className="text-decoration-none text-secondary"
                      href={`tel: ${config.phone}`}
                    >
                      +91-{config.phone}
                    </a>
                  </b>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Header;
